import * as XLSX from 'xlsx';

export const getMealsDetails = (meal) => {
  if (!meal) return;
  const ingredients_list = meal?.ingredients_list?.map((data) => {
    return {
      description: data.description,
      quantity: data.quantity,
      details: {
        id: data.ingredient._id,
        name: data.ingredient.name
      },
      unit: {
        id: data?.unit?.id,
        name: data?.unit?.name
      }
    };
  });

  const ingredientsArray = meal?.ingredients?.map((data) => ({
    value: data
  }));
  // Handle both old string format and new object format for instruction_steps
  const instructionStepsArray =
    meal?.instruction_steps?.length > 0
      ? meal.instruction_steps.map((data) => {
          // If it's already an object with the new structure, use it as is
          if (typeof data === 'object' && data.step !== undefined) {
            return {
              step: data.step || '',
              time_estimate: data.time_estimate,
              image_url: data.image_url,
              video_url: data.video_url
            };
          }
          // If it's a string (old format), convert it
          return {
            step: data || '',
            time_estimate: undefined,
            image_url: undefined,
            video_url: undefined
          };
        })
      : meal?.instructions?.map((data) => ({
          // Fallback to old instructions field if instruction_steps doesn't exist
          step: typeof data === 'string' ? data : '',
          time_estimate: undefined,
          image_url: undefined,
          video_url: undefined
        })) || [
          {
            step: '',
            time_estimate: undefined,
            image_url: undefined,
            video_url: undefined
          }
        ];
  const countriesData = meal?.countries?.map((data) => ({
    value: data,
    label: data
  }));

  const types = meal?.types?.map((data) => ({ value: data, label: data }));
  const categories = meal?.categories?.map((data) => ({
    value: data._id,
    label: data.name
  }));

  const health = meal?.health_conditions?.map((data) => ({
    value: data._id,
    label: data.name
  }));
  const nutrientsArray = meal?.nutrients?.map((data) => ({
    name: data.name,
    value: data.value
  }));

  const incompatible_medical_conditions = meal?.incompatible_medical_conditions?.map(
    (data) => ({
      value: data
    })
  );

  const meal_tags = meal?.tags?.map((data) => ({
    label: data.name,
    value: data._id
  }));
  const product_groups = meal?.product_groups?.map((item) => ({
    value: item.name,
    id: item._id
  }));

  return {
    ingredients_list,
    ingredientsArray,
    instructionStepsArray,
    countriesData,
    types,
    categories,
    health,
    nutrientsArray,
    incompatible_medical_conditions,
    meal_tags,
    product_groups
  };
};

export const isStringArray = (value) => {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
};

export const handleSubmitData = (doc) => {
  let ingredientsArray = Object.values(doc.ingredients);
  let instructionStepsArray = doc.instruction_steps || [];
  let stringArray = ingredientsArray.map((data) => data.value);
  // Format instruction_steps to the new object structure
  let formattedInstructionSteps = instructionStepsArray.map((data) => {
    const stepData = {
      step: data.step || ''
    };
    if (data.time_estimate) {
      stepData.time_estimate = Number(data.time_estimate);
    }
    if (data.image_url) {
      stepData.image_url = data.image_url;
    }
    if (data.video_url) {
      stepData.video_url = data.video_url;
    }
    return stepData;
  });
  const types = doc.types.map(
    (doc) => doc.value.charAt(0).toUpperCase() + doc.value.slice(1)
  );
  const countries =
    Array.isArray(doc.countries) &&
    doc.countries.length > 0 &&
    typeof doc.countries[0] === 'object' &&
    'value' in doc.countries[0]
      ? doc.countries.map((doc) => doc.value)
      : doc.countries;
  const categories = doc.categories.map((doc) => doc.value).filter(Boolean);
  const health = doc.health_conditions.map((doc) => doc.value).filter(Boolean);
  const incompatible_medical_conditions = (doc.incompatible_medical_conditions || [])
    .map((item) => item.value)
    .filter(Boolean);
  const tags = doc.tags.map((doc) => doc.value).filter(Boolean);
  const ingredients_list = doc.ingredients_list.some(
    (data) => !data.details.id || !data.quantity || !data.unit.id
  )
    ? []
    : doc.ingredients_list.map((data) => {
        const ingredientData = {
          ingredient: data.details.id,
          quantity: Number(data.quantity),
          unit: data.unit.id
        };

        if (data?.description?.trim() !== '') {
          ingredientData.description = data.description;
        }

        return ingredientData;
      });
  const product_groups = doc.product_groups
    .filter((item) => item.value !== '' && item.id !== '')
    .map((item) => item.id);
  return {
    stringArray,
    formattedInstructionSteps,
    types,
    categories,
    health,
    incompatible_medical_conditions,
    tags,
    countries,
    ingredients_list,
    product_groups
  };
};

function splitOutsideParens(str) {
  const parts = [];
  let depth = 0;
  let current = '';

  for (const char of str) {
    if (char === '(') depth++;
    if (char === ')') depth--;

    if (char === ',' && depth === 0) {
      parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts;
}

export const parseMealDataExcel = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const data = XLSX.utils.sheet_to_json(sheet);

  const transformedData = data.map((row) => {
    const instructionSteps = row?.instruction_steps
      ?.split(';')
      .map((item) => {
        const [step, time_estimate, image_url, video_url] = item
          .trim()
          .split('|')
          .map((s) => s?.trim());

        const parsedTime = parseFloat(time_estimate);

        const stepData = {
          step: step !== 'step' ? step : '',
          time_estimate: Number.isNaN(parsedTime) ? 0 : parsedTime
        };

        if (video_url && video_url !== 'video_url') {
          stepData.video_url = video_url;
        }
        if (image_url && image_url !== 'image_url') {
          stepData.image_url = image_url;
        }

        return stepData;
      })
      .filter((step) => step.step !== '');
    const nutrientsRaw = row?.nutrients || '';
    const ingredientsRaw = row?.ingredients_list || '';
    const typeRaw = row?.types || '';

    const nutrients = nutrientsRaw.split(',').map((i) => {
      const [name, value] = i.trim()?.split(':');
      const data = {
        name: name === 'name' ? '' : name,
        value: value === 'value' ? '' : name
      };

      return data;
    });
    const ingredientsParts = ingredientsRaw.split(',').map((i) => {
      const [ingredient_id, description, quantity, unit_id] = i
        .trim()
        .split('|')
        .map((s) => s?.trim());

      const parsedQty = parseFloat(quantity);

      const ingredientData = {
        quantity: Number.isNaN(parsedQty) ? 0 : parsedQty
      };

      if (ingredient_id) {
        ingredientData.ingredient = ingredient_id;
      }

      if (unit_id) {
        ingredientData.unit = unit_id;
      }

      if (description) {
        ingredientData.description = description;
      }

      return ingredientData;
    });

    const type = typeRaw.split(',').map((i) => {
      const cleaned = i.replace(/['"]/g, '').trim();
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    });

    return {
      ...row,
      images: row?.image || '',
      product_groups: (row?.product_groups || '')
        .split(',')
        .map((i) => i.replace(/['"]/g, '').trim())
        .filter(Boolean),
      categories: (row?.categories || '')
        .split(',')
        .map((i) => i.replace(/['"]/g, '').trim())
        .filter(Boolean),
      health_conditions: (row?.health_conditions || '')
        .split(',')
        .map((i) => i.replace(/['"]/g, '').trim())
        .filter(Boolean),
      countries: (row?.countries || '')
        .split(',')
        .map((i) => i.replace(/['"]/g, '').trim())
        .map((c) => c.charAt(0).toUpperCase() + c.slice(1))
        .filter(Boolean),
      tags: (row?.tags || '')
        .split(',')
        .map((i) => i.replace(/['"]/g, '').trim())
        .filter(Boolean),
      types: type,
      nutrients: nutrients?.some((data) => !data.name || !data.value)
        ? []
        : nutrients,
      ingredients_list: ingredientsParts.filter(
        (data) => data.quantity > 0 && (data.ingredient || data.description)
      ),
      instruction_steps: instructionSteps,
      ingredients: splitOutsideParens(row?.ingredients || '')
        .map((i) => i.trim())
        .filter(Boolean)
    };
  });

  return transformedData;
};

export const generateExcelHeaders = (type, meals = []) => {
  if (type === 'update') {
    return meals.map((meal) => ({
      id: meal?._id || '',
      name: meal?.name || '',
      description: meal?.description || '',
      notification_message: meal?.notification_message || '',
      lu_tips: meal?.lu_tips || '',
      video_url: meal?.video_url || '',
      image: meal?.image || '',

      fat: meal?.fat ?? 0,
      protein: meal?.protein ?? 0,
      carbohydrate: meal?.carbohydrate ?? 0,
      prep_time: meal?.prep_time ?? 0,

      types: meal?.types?.join(', ') || '',
      categories: meal?.categories?.join(', ') || '',
      countries: meal?.countries?.join(', ') || '',
      health_conditions: meal?.health_conditions?.join(', ') || '',

      tags: meal?.tags?.map((item) => item?._id).join(', ') || '',

      product_groups: meal?.product_groups?.join(', ') || '',

      ingredients: meal?.ingredients?.join(', ') || '',

      nutrients:
        meal?.nutrients
          ?.map((item) => `${item?.name || 'name'}:${item?.value || 'value'}`)
          .join(', ') || '',

      ingredients_list:
        meal?.ingredients_list
          ?.map(
            (item) =>
              `${item?.ingredient?._id || 'ingredient_id'}|${
                item?.description || 'description'
              }|${item?.quantity || 'quantity'}|${item?.unit?._id || 'unit_id'}`
          )
          .join(', ') || '',

      instruction_steps:
        meal?.instruction_steps
          ?.map(
            (item) =>
              `${item?.step || 'step'}|${item?.time_estimate ?? 'time_estimate'}|${
                item?.image_url || 'image_url'
              }|${item?.video_url || 'video_url'}`
          )
          .join('; ') || ''
    }));
  }

  return [
    {
      name: '',
      description: '',
      notification_message: '',
      lu_tips: '',
      video_url: '',
      image: '',

      fat: 0,
      protein: 0,
      carbohydrate: 0,
      prep_time: 0,

      types: '',
      categories: '',
      countries: '',
      health_conditions: '',

      tags: '',

      product_groups: '',

      ingredients: '',

      nutrients: 'name:value, name:value',

      ingredients_list:
        'ingredient_id|description|quantity|unit_id, ingredient_id|description|quantity|unit_id',

      instruction_steps:
        'step|time_estimate|image_url|video_url; step|time_estimate|image_url|video_url'
    }
  ];
};
